import {computed, type ComputedRef, inject, type InjectionKey, provide, type Ref, ref, watch} from "vue";
import type {AgendaDescriptor, AgendaRegistryEntry} from "@t/agenda/Agenda";
import type {FilterPart} from "@t/fd/editor/Filter";
import {useAgendaStore} from "@/store/AgendaStore.ts";


export const descriptorKey = Symbol() as InjectionKey<ComputedRef<AgendaDescriptor>>;
export const registrationKey = Symbol() as InjectionKey<Ref<AgendaRegistryEntry|null>>;


type ParserState = "beforetoken" | "aftertoken" | "item" | "qitem" ;

function slashChar(ch:string):string {
	switch (ch) {
		case "t":
			return "\t";
		case "r":
			return "\r";
		case "n":
			return "\n";
		case "\"":
			return "\"";
		case "'":
			return "'";
		case ",":
			return ",";
	}
	return null;
}

/**
 * Test if the string contains spec. character and must be quoted when emitted
 */
function hasSpecialChar(s:string):boolean {
	for (let i = 0; i < s.length; i++) {
		let ch = s[i];
		if (ch === ' ' || ch === '\n' || ch === '\r' || ch === '\t' || ch === ',' || ch === '\'' || ch === '\"') return true;
	}
	return false;
}

function escapeChar(ch:string):string {
	switch (ch) {
		case '\n': return '\\n';
		case '\r': return '\\r';
		case '\t': return '\\t';
		case '\'': return '\\\'';
		case '"': return '\\"';
	}
	return ch;
}

export function textToArray(input: string|null|undefined): string[] {
	const result: string[] = [];
	if (input == null) return result;
	input = input.trim();
	if (input.length === 0) return result;

	let state:ParserState = "beforetoken";
	let q = "";			// character used to open/close quotes
	let buff = "";		// token buffer
	let trim = true;	// token in buff should be trimmed before putting to the result[]

	for (let i = 0; i < input.length; i++) {
		let ch = input[i];

		switch (state) {
			case "beforetoken":
				if (ch === '"' || ch === '\'') {
					// Quoted token - must be closed with the same character
					state = "qitem";
					trim = false;
					q = ch;
				} else if (ch === ',') {
					// empty token
					result.push("");
					buff = "";
				} else if (ch === '\\') {
					// token isn't quoted but starts with escaped character
					if ((i + 1) < input.length) {
						ch = slashChar(input[++i]);
						if (ch == null) {
							// not a valid escape char
							i--;
							buff = '\\';
						} else {
							buff = ch;
						}
					} else buff = '\\';
					state = "item";
					trim = true;
				} else if (!/\s/.test(ch)) {
					// non-white space and not a quote = this starts unquoted item which will be trimmed
					buff = ch;
					state = "item";
					trim = true;
				} // the rest is white space (we stay in this state)
				break;
			case "aftertoken":
				if (ch === ',') {
					result.push(buff);
					buff = "";
					state = "beforetoken";
				}
				// ignore characters after qoute but before comma
				break;
			case "item":
				// Unquoted item
				if (ch === ',') {
					// end of item
					result.push(buff.trim());
					buff = "";
					state = "beforetoken";
				} else if (ch === '\\') {
					if ((i + 1) < input.length) {
						ch = slashChar(input[++i]);
						if (ch == null) {
							i--;
							buff += '\\';
						} else {
							buff += ch;
						}
					} else buff += '\\';
				} else {
					buff += ch;
				}
				break;
			case "qitem":
				if (ch === q) {
					state = "aftertoken";
				} else  if (ch === '\\') {
					if ((i + 1) < input.length) {
						ch = slashChar(input[++i]);
						if (ch == null) {
							i--;
							buff += '\\';
						} else {
							buff += ch;
						}
					} else buff += '\\';
				} else {
					buff += ch;
				}
				break;
		}
	}
	// No more characters
	switch (state) {
		case "aftertoken":
			result.push(buff);
			break;
		case "item":
			result.push(buff.trim());
			break;
		case "qitem":
			// technically this is error (last qouted item wasn't closed)
			result.push(buff);
			break;
	}
	return result;
}

function testMe(str:string|null|undefined):void {
	let arr = textToArray(str);
	console.log('['+str+'] = \n'+JSON.stringify(arr)+"\n["+arrayToText(arr)+"]");
}

// console.log("-------------------------");
// testMe("5");
// testMe("5,10,   ahoj  , 'vole");
// testMe(",,");
// testMe("\"\"");
// testMe("1, 2, 4, 18, '-', 0");
// console.log("-------------------------");


function quoteItem(item: string, q = '"') {
	let b = q;
	for (let i = 0; i < item.length; i++) {
		b += escapeChar(item[i]);
	}
	b += q;
	return b;
}

export function arrayToText(items: string[]): string {
	if (!Array.isArray(items)) return "";
	return items.map(item => {
		if (item.length === 0) return '""';	// empty strings will be quoted
		// Determine if the item contains spaces, commas, single quotes, or double quotes
		const hasSpecialChars = hasSpecialChar(item);
		if (hasSpecialChars) return quoteItem(item);
		return item;
	}).join(', ');
}

export const MAX_ARRAY_VALUES = 50;


export function useFilter(fp:ComputedRef<FilterPart>) {

	const ast = useAgendaStore();
	const registration = ref<AgendaRegistryEntry|null>(null);
	provide(descriptorKey, computed<AgendaDescriptor>(() => fp.value.descriptor));

	watch(() => fp.value.descriptor, async (desc) => {
		registration.value = await ast.lookupRegistry(fp.value.descriptor);
	}, { deep: true, immediate: true });

	provide(registrationKey, registration);

	return {
		registration,	// registration of currenct agenda (based on descriptor provided in the filter part */
    }
}
